import axios from "axios";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

/**
 * @param {string} name
 * @returns {Promise<import('../types/invoiceManager.types').InvoiceCustomerSearchRow[]>}
 */
export async function searchInvoiceCustomers(name) {
  const q = String(name || "").trim();
  if (q.length < 2) return [];

  const { data } = await axios.get(
    `${getApiBaseUrl()}InvoiceManager/SearchCustomers`,
    {
      params: { name: q },
      headers: getAuthHeaders(),
    },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * @param {{ senderName: string, senderEmail?: string }} params
 * @returns {Promise<import('../types/invoiceManager.types').InvoiceParcelRow[]>}
 */
export async function getParcelsByCustomer({ senderName, senderEmail } = {}) {
  const name = String(senderName || "").trim();
  if (!name) return [];

  const params = { senderName: name };
  const email = String(senderEmail || "").trim();
  if (email) params.senderEmail = email;

  const { data } = await axios.get(
    `${getApiBaseUrl()}InvoiceManager/GetParcelsByCustomer`,
    {
      params,
      headers: getAuthHeaders(),
    },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number|string} parcelId
 * @returns {Promise<import('../types/invoiceManager.types').InvoiceVersionRow[]>}
 */
export async function getInvoicesByParcelId(parcelId) {
  if (parcelId == null || parcelId === "") return [];

  const { data } = await axios.get(
    `${getApiBaseUrl()}InvoiceManager/GetInvoicesByParcelId/${parcelId}`,
    { headers: getAuthHeaders() },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Payment timeline for a parcel (paid + pending events, ordered by paymentDate then creationDate).
 * @param {number|string} parcelId
 * @returns {Promise<import('../types/invoiceManager.types').ParcelPaymentHistory|null>}
 */
export async function getPaymentHistoryByParcelId(parcelId) {
  if (parcelId == null || parcelId === "") return null;

  const { data } = await axios.get(
    `${getApiBaseUrl()}InvoiceManager/GetPaymentHistoryByParcelId/${parcelId}`,
    { headers: getAuthHeaders() },
  );
  if (!data || typeof data !== "object") return null;
  return {
    ...data,
    items: Array.isArray(data.items) ? data.items : [],
  };
}

/**
 * Deep-link / enrich a single payment row by invoice number.
 * @param {string} invoiceNo
 */
export async function getPaymentByInvoiceNo(invoiceNo) {
  const no = String(invoiceNo || "").trim();
  if (!no) return null;

  const { data } = await axios.get(
    `${getApiBaseUrl()}Payment/GetPaymentByInvoiceNo/${encodeURIComponent(no)}`,
    { headers: getAuthHeaders() },
  );
  return data || null;
}

/**
 * Historical invoice header + product line snapshots for view/print.
 * Falls back to live parcel details only when no snapshot exists (legacy).
 * @param {string} invoiceNo
 * @returns {Promise<import('../types/invoiceManager.types').InvoiceForPrint|null>}
 */
export async function getInvoiceForPrint(invoiceNo) {
  const no = String(invoiceNo || "").trim();
  if (!no) return null;

  const { data } = await axios.get(
    `${getApiBaseUrl()}InvoiceManager/GetInvoiceForPrint/${encodeURIComponent(no)}`,
    { headers: getAuthHeaders() },
  );
  return data || null;
}
